export type ChatTranscriptMessage = Record<string, unknown> & {
  reactId?: unknown;
  question?: unknown;
  textAnswer?: unknown;
  createdDate?: unknown;
  updatedDate?: unknown;
};

export type ChatTranscriptEntry = ChatTranscriptMessage & {
  answers: string[];
  createdDate: number;
};

/**
 * Build the newest-first transcript without collapsing separate turns that
 * happen to use the same short prompt (for example, "yes" or "try again").
 * Duplicate persisted revisions are merged only when they share a reactId.
 */
export function buildChatTranscriptEntries(
  history: readonly ChatTranscriptMessage[],
): ChatTranscriptEntry[] {
  const grouped = new Map<string, ChatTranscriptEntry>();

  [...history].reverse().forEach((message, reverseIndex) => {
    const question = String(message?.question ?? "").trim();
    const answer = String(message?.textAnswer ?? "").trim();
    if (!question && !answer) return;

    const reactId = typeof message?.reactId === "string" ? message.reactId.trim() : "";
    // Legacy rows without a reactId are independent turns. Never merge them by
    // question text because repeated confirmations are common in real chats.
    const key = reactId || `legacy-turn-${reverseIndex}`;
    const timestamp = Number(message.createdDate ?? message.updatedDate ?? 0) || 0;
    const existing = grouped.get(key);

    if (existing) {
      if (answer && !existing.answers.includes(answer)) existing.answers.push(answer);
      existing.createdDate = Math.max(existing.createdDate, timestamp);
      return;
    }

    grouped.set(key, {
      ...message,
      answers: answer ? [answer] : [],
      createdDate: timestamp,
    });
  });

  return Array.from(grouped.values());
}
